import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { buildMetadata } from "@/lib/metadata";
import { useAuth } from "@/providers/auth";
import { AgentConnections } from "@/routes/_main/settings/-components/agent-connections";
import UserTabContent from "@/routes/_main/settings/-components/user-tabs-content";
import { WhatsAppChannels } from "@/routes/_main/settings/-components/whatsapp-channels";
import { InvitationsService } from "@/services/invitation-service";
import { SupplierUserService } from "@/services/supplier-user-service";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/_main/settings/")({
  component: RouteComponent,
  head: () => ({
    meta: buildMetadata({
      title: "Settings",
      description: "View your settings",
      keywords: ["settings", "account", "view"],
    }),
  }),
});

function RouteComponent() {
  const [activeTab, setActiveTab] = useState("general");
  const { user } = useAuth();
  const isSupplierAdmin = user?.supplierRole === "supplier_admin";

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const isSuccess =
      search.get("zoho_success") === "1" || search.get("ms365_success") === "1";
    if (isSuccess) {
      // toast({ title: "Zoho connected", description: "Your Zoho account is now linked." });
      // strip the query param
      void navigate({ to: "/settings", replace: true });
    }
  }, [location, navigate]);

  const { data: supplierUsers } = useQuery({
    queryKey: ["user-suppliers"],
    queryFn: SupplierUserService.getSupplierUsers,
  });

  const { data: invitations } = useQuery({
    queryKey: ["user-invitations"],
    queryFn: InvitationsService.getInvitations,
  });

  return (
    <div className="flex min-h-screen flex-col gap-4 overflow-y-scroll p-4 md:p-8">
      <div className="md:mt-8">
        <Tabs
          defaultValue="general"
          value={activeTab}
          onValueChange={handleTabChange}
          className="custom-tabs"
        >
          <TabsList className="flex max-w-full justify-start overflow-x-auto bg-transparent">
            <TabsTrigger
              value="general"
              className="!rounded-none relative border-b-2 border-b-gray-200 px-2 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none md:px-8"
            >
              General
            </TabsTrigger>
            <TabsTrigger
              value="users"
              className="!rounded-none relative border-b-2 border-b-gray-200 px-2 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none md:px-8"
            >
              Users
            </TabsTrigger>
            <TabsTrigger
              value="notifications"
              className="!rounded-none relative border-b-2 border-b-gray-200 px-2 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none md:px-8"
              disabled={true}
            >
              Notifications
            </TabsTrigger>
            {isSupplierAdmin ? (
              <TabsTrigger
                value="whatsapp-channels"
                aria-label="WhatsApp channels"
                className="!rounded-none relative border-b-2 border-b-gray-200 px-3 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none md:px-8"
              >
                <span aria-hidden="true" className="md:hidden">
                  WhatsApp
                </span>
                <span aria-hidden="true" className="hidden md:inline">
                  WhatsApp channels
                </span>
              </TabsTrigger>
            ) : null}
            {isSupplierAdmin ? (
              <TabsTrigger
                value="agent-connections"
                className="!rounded-none relative border-b-2 border-b-gray-200 px-3 py-2 text-gray-600 data-[state=active]:border-b-2 data-[state=active]:border-b-black data-[state=active]:font-medium data-[state=active]:text-black data-[state=active]:shadow-none md:px-8"
              >
                AI Agents
              </TabsTrigger>
            ) : null}
          </TabsList>

          <TabsContent value="general">
            <UserTabContent
              activeTab="general"
              supplierUsers={supplierUsers!}
              invitations={invitations!}
            />
          </TabsContent>

          <TabsContent value="users">
            <UserTabContent
              activeTab="users"
              supplierUsers={supplierUsers!}
              invitations={invitations!}
            />
          </TabsContent>

          <TabsContent value="notifications">
            <UserTabContent
              activeTab="notifications"
              supplierUsers={supplierUsers!}
              invitations={invitations!}
            />
          </TabsContent>
          {isSupplierAdmin ? (
            <TabsContent value="whatsapp-channels">
              <WhatsAppChannels />
            </TabsContent>
          ) : null}
          {isSupplierAdmin ? (
            <TabsContent value="agent-connections">
              <AgentConnections />
            </TabsContent>
          ) : null}
        </Tabs>
      </div>
    </div>
  );
}
